import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { fmtRoDate } from "../../lib/dateFmt";

/**
 * Dialog pentru modificarea perioadei unei regii sau a unui proiect
 * — modifică start_date ȘI/sau end_date.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - card: { type: "regie" | "project", ref_id: string, label: string }
 *  - currentStartDate?: string (YYYY-MM-DD)
 *  - currentEndDate?: string (YYYY-MM-DD) — pentru proiecte/regii cu dată finală
 *  - currentIndeterminateEnd?: boolean — pentru regii cu perioadă nedeterminată
 *  - onSaved?: ({ new_start_date, new_end_date, deleted_entries, truncated_entries }) => void
 */
export default function PostponeDialog({
  open, onClose, card,
  currentStartDate, currentEndDate, currentIndeterminateEnd,
  earliestAllocationDate,  // 🟢 cea mai veche dată cu oameni alocați — blochează postponere după ea
  onSaved,
}) {
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [indetEnd, setIndetEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const isRegie = card?.type === "regie";

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setNewStart(currentStartDate || today);
    setNewEnd(currentEndDate || "");
    setIndetEnd(!!currentIndeterminateEnd);
    setHistory([]);
    if (card?.ref_id) {
      const url = isRegie
        ? `/regii/${card.ref_id}/postpone-history`
        : `/projects/${card.ref_id}/postpone-history`;
      api.get(url)
        .then((r) => setHistory(r.data?.rows || []))
        .catch(() => setHistory([]));
    }
  }, [open, currentStartDate, currentEndDate, currentIndeterminateEnd, card?.ref_id, isRegie]);

  const submit = async () => {
    if (!newStart || newStart.length !== 10) {
      toast.error("Selectează o dată de început validă");
      return;
    }
    if (!indetEnd && newEnd && newEnd.length !== 10) {
      toast.error("Selectează o dată de sfârșit validă");
      return;
    }
    if (!indetEnd && newEnd && newEnd < newStart) {
      toast.error("Data de sfârșit nu poate fi înainte de început");
      return;
    }
    // 🟢 Blochează postponere dacă există deja oameni alocați înainte de noul start
    if (earliestAllocationDate && newStart > earliestAllocationDate) {
      const dStr = fmtRoDate(earliestAllocationDate);
      toast.error(
        `❌ Nu poți muta startul mai târziu de ${dStr} — există deja oameni alocați pe această ${isRegie ? "regie" : "proiect"} începând cu acea dată. Ștergeți întâi alocările vechi sau alegeți o dată mai devreme.`
      );
      return;
    }
    const startChanged = newStart !== (currentStartDate || "");
    const endChanged = indetEnd
      ? !currentIndeterminateEnd
      : ((newEnd || "") !== (currentEndDate || ""));
    if (!startChanged && !endChanged) {
      toast.info("Nicio schimbare");
      return;
    }
    setBusy(true);
    try {
      const url = isRegie
        ? `/regii/${card.ref_id}/postpone`
        : `/projects/${card.ref_id}/postpone`;
      const payload = { new_start_date: newStart };
      if (indetEnd) {
        if (isRegie) {
          payload.indeterminate_end = true;
        } else {
          // proiectele nu suportă „nedeterminat" — ignorăm
          payload.new_end_date = newEnd || undefined;
        }
      } else if (newEnd) {
        payload.new_end_date = newEnd;
      }
      const r = await api.patch(url, payload);
      const del = r?.data?.deleted_entries || 0;
      const trunc = r?.data?.truncated_entries || 0;
      const ext = r?.data?.extended_entries || 0;
      const parts = [];
      if (startChanged) {
        const dir = currentStartDate && newStart < currentStartDate ? "Început mutat mai devreme" : "Început amânat";
        parts.push(`${dir} la ${fmtRoDate(newStart)}`);
      }
      if (endChanged) {
        if (indetEnd) parts.push("Sfârșit: perioadă nedeterminată");
        else if (newEnd) parts.push(`Sfârșit setat la ${fmtRoDate(newEnd)}`);
      }
      if (del > 0) parts.push(`${del} alocări eliberate`);
      if (trunc > 0) parts.push(`${trunc} alocări ajustate`);
      if (ext > 0) parts.push(`${ext} alocări prelungite până la noul sfârșit`);
      toast.success(parts.join(" · "));
      onSaved && onSaved({
        new_start_date: newStart,
        new_end_date: indetEnd ? null : (newEnd || null),
        deleted_entries: del,
        truncated_entries: trunc,
      });
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  if (!card) return null;

  const movingStartBackward = currentStartDate && newStart && newStart < currentStartDate;
  const movingStartForward = currentStartDate && newStart && newStart > currentStartDate;
  const endShortened = currentEndDate && newEnd && newEnd < currentEndDate && !indetEnd;
  const endExtended = currentEndDate && newEnd && newEnd > currentEndDate && !indetEnd;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-md" data-testid="postpone-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            📅 Modifică perioada {isRegie ? "regiei" : "proiectului"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Editează data de început și/sau data de final. Alocările din afara noii perioade vor fi ajustate sau șterse automat.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm">
            <div className="text-zinc-500 text-xs uppercase tracking-wider font-semibold mb-1">
              {isRegie ? "Regie" : "Proiect"}
            </div>
            <div className="font-semibold text-zinc-800" data-testid="postpone-card-label">
              {card.label}
            </div>
          </div>

          {(currentStartDate || currentEndDate || currentIndeterminateEnd) && (
            <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-2 py-1.5 space-y-0.5">
              {currentStartDate && (
                <div>Început actual: <span className="font-mono font-semibold">{fmtRoDate(currentStartDate)}</span></div>
              )}
              {currentIndeterminateEnd ? (
                <div>Sfârșit actual: <span className="font-semibold text-emerald-700">♾ perioadă nedeterminată</span></div>
              ) : currentEndDate ? (
                <div>Sfârșit actual: <span className="font-mono font-semibold">{fmtRoDate(currentEndDate)}</span></div>
              ) : null}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">📅 Nouă dată de început</Label>
              <Input
                type="date"
                value={newStart}
                onChange={(e) => setNewStart(e.target.value)}
                className="h-9 text-sm mt-1"
                data-testid="postpone-new-date-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">📅 Nouă dată de sfârșit</Label>
              <Input
                type="date"
                value={newEnd}
                onChange={(e) => setNewEnd(e.target.value)}
                disabled={indetEnd}
                min={newStart || undefined}
                className="h-9 text-sm mt-1 disabled:bg-zinc-100"
                data-testid="postpone-new-end-date-input"
              />
            </div>
          </div>

          {isRegie && (
            <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer select-none">
              <Checkbox
                checked={indetEnd}
                onCheckedChange={(v) => setIndetEnd(!!v)}
                data-testid="postpone-indeterminate-end"
              />
              <span>♾ Fără dată de sfârșit (perioadă nedeterminată)</span>
            </label>
          )}

          {/* 🟢 Blocaj vizual: postponere blocată dacă există alocări înainte de noul start */}
          {earliestAllocationDate && newStart && newStart > earliestAllocationDate && (
            <div className="text-xs bg-red-50 border-2 border-red-400 rounded px-2 py-2 text-red-900 font-semibold" data-testid="postpone-blocked-warning">
              ❌ <strong>Postponere blocată</strong>: există deja oameni alocați începând cu <b>{fmtRoDate(earliestAllocationDate)}</b>. Nu poți muta startul după această dată — șterge mai întâi alocările vechi din planificator.
            </div>
          )}
          {movingStartForward && (
            <div className="text-xs bg-amber-50 border border-amber-300 rounded px-2 py-2 text-amber-900">
              ⚠ Alocările dintre <b>{fmtRoDate(currentStartDate)}</b> și <b>{fmtRoDate(newStart)}</b> vor fi <b>șterse</b> (echipa eliberată).
            </div>
          )}
          {movingStartBackward && (
            <div className="text-xs bg-blue-50 border border-blue-300 rounded px-2 py-2 text-blue-900">
              ℹ Mutare mai devreme: alocările existente rămân la datele lor.
            </div>
          )}
          {endShortened && (
            <div className="text-xs bg-amber-50 border border-amber-300 rounded px-2 py-2 text-amber-900">
              ⚠ Alocările după <b>{fmtRoDate(newEnd)}</b> vor fi <b>șterse</b> sau trunchiate.
            </div>
          )}
          {endExtended && (
            <div className="text-xs bg-emerald-50 border border-emerald-300 rounded px-2 py-2 text-emerald-900">
              ✓ Sfârșitul extins — vei putea aloca echipă pe noile zile.
            </div>
          )}

          {history.length > 0 && (
            <div className="border-t border-zinc-200 pt-2 mt-2" data-testid="postpone-history-block">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5">
                🕒 Istoric modificări ({history.length})
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
                {history.slice(0, 10).map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center gap-1.5 text-[11px] text-zinc-700 bg-zinc-50 rounded px-2 py-1"
                    data-testid={`postpone-history-row-${h.id}`}
                  >
                    <span className="font-mono text-zinc-500 flex-shrink-0">
                      {(h.created_at || "").slice(0, 10)}
                    </span>
                    <span className="text-zinc-400">·</span>
                    <span className="font-mono">
                      {fmtRoDate(h.old_start_date) || "—"} → <b className="text-amber-700">{fmtRoDate(h.new_start_date)}</b>
                      {(h.old_end_date || h.new_end_date) && (
                        <span className="ml-1 text-zinc-500">
                          | sf: {fmtRoDate(h.old_end_date) || "—"} → <b className="text-amber-700">{fmtRoDate(h.new_end_date) || "♾"}</b>
                        </span>
                      )}
                    </span>
                    <span className="text-zinc-400 ml-auto truncate text-[10px]">{h.user_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onClose && onClose()}
            disabled={busy}
            data-testid="postpone-cancel-btn"
          >
            Anulează
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !newStart || !!(earliestAllocationDate && newStart > earliestAllocationDate)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="postpone-confirm-btn"
          >
            {busy ? "Se salvează…" : "💾 Salvează perioada"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
